import React, { useState, useEffect } from 'react';
import { Mail, Plus, Edit2, Trash2, Power, Save, X, AlertCircle, CheckCircle } from 'lucide-react';
import { EmailNotificationJobService } from '../services/emailNotificationJobService';
import { EmailNotificationJob, EmailNotificationEvent } from '../types';
import { SearchableMultiSelect } from './SearchableMultiSelect';

interface EmailNotificationJobsManagerProps {
  partnerId: string;
  userId?: string;
  userName?: string; // Reserved for future activity logging
}

// Available event options with descriptions
const EVENT_OPTIONS = [
  {
    value: 'campaign_status_updates' as EmailNotificationEvent,
    label: 'Campaign Status Updates',
    description: 'Notified when campaign status changes (active, paused, completed)'
  },
  {
    value: 'campaign_created' as EmailNotificationEvent,
    label: 'Campaign Created',
    description: 'Notified when a new campaign is created'
  },
  {
    value: 'campaign_updated' as EmailNotificationEvent,
    label: 'Campaign Updated',
    description: 'Notified when campaign details are modified'
  },
  {
    value: 'promo_code_requested' as EmailNotificationEvent,
    label: 'Promo Code Requested',
    description: 'Notified when a user requests a promo code'
  },
  {
    value: 'advertisement_created' as EmailNotificationEvent,
    label: 'New Advertisement Created',
    description: 'Notified when a new advertisement is created'
  },
  {
    value: 'advertisement_approved' as EmailNotificationEvent,
    label: 'Advertisement Approved',
    description: 'Notified when an advertisement is approved'
  },
  {
    value: 'advertisement_rejected' as EmailNotificationEvent,
    label: 'Advertisement Rejected',
    description: 'Notified when an advertisement is rejected'
  }
];

export const EmailNotificationJobsManager: React.FC<EmailNotificationJobsManagerProps> = ({
  partnerId,
  userId
  // userName reserved for future activity logging
}) => {
  const [jobs, setJobs] = useState<EmailNotificationJob[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingJob, setEditingJob] = useState<EmailNotificationJob | null>(null);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Form state
  const [formData, setFormData] = useState({
    job_name: '',
    recipient_emails: '',
    event_subscriptions: [] as EmailNotificationEvent[],
    enabled: true
  });

  useEffect(() => {
    loadJobs();
  }, [partnerId]);

  const loadJobs = async () => {
    try {
      setLoading(true);
      const jobsList = await EmailNotificationJobService.getJobs(partnerId);
      setJobs(jobsList);
    } catch (error) {
      console.error('Failed to load email notification jobs:', error);
      showMessage('error', 'Failed to load notification jobs');
    } finally {
      setLoading(false);
    }
  };

  const showMessage = (type: 'success' | 'error', text: string) => {
    setMessage({ type, text });
    setTimeout(() => setMessage(null), 3000);
  };

  const openCreateModal = () => {
    setEditingJob(null);
    setFormData({
      job_name: '',
      recipient_emails: '',
      event_subscriptions: [],
      enabled: true
    });
    setShowModal(true);
  };

  const openEditModal = (job: EmailNotificationJob) => {
    setEditingJob(job);
    setFormData({
      job_name: job.job_name,
      recipient_emails: job.recipient_emails.join(', '),
      event_subscriptions: job.event_subscriptions,
      enabled: job.enabled
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingJob(null);
    setFormData({
      job_name: '',
      recipient_emails: '',
      event_subscriptions: [],
      enabled: true
    });
  };

  const handleSave = async () => {
    try {
      // Validate job name
      if (!formData.job_name.trim()) {
        showMessage('error', 'Please enter a job name');
        return;
      }

      // Parse and validate email addresses
      const emails = formData.recipient_emails
        .split(/[,;]/)
        .map(e => e.trim())
        .filter(e => e.length > 0);

      if (emails.length === 0) {
        showMessage('error', 'Please add at least one email address');
        return;
      }

      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      const invalidEmails = emails.filter(e => !emailRegex.test(e));
      if (invalidEmails.length > 0) {
        showMessage('error', `Invalid email addresses: ${invalidEmails.join(', ')}`);
        return;
      }

      // Validate event subscriptions
      if (formData.event_subscriptions.length === 0) {
        showMessage('error', 'Please select at least one event subscription');
        return;
      }

      let result;
      if (editingJob) {
        // Update existing job
        result = await EmailNotificationJobService.updateJob(editingJob.id, {
          job_name: formData.job_name,
          recipient_emails: emails,
          event_subscriptions: formData.event_subscriptions,
          enabled: formData.enabled
        });
      } else {
        // Create new job
        result = await EmailNotificationJobService.createJob(partnerId, {
          job_name: formData.job_name,
          recipient_emails: emails,
          event_subscriptions: formData.event_subscriptions,
          enabled: formData.enabled,
          created_by: userId
        });
      }

      if (result) {
        showMessage('success', `Notification job ${editingJob ? 'updated' : 'created'} successfully!`);
        closeModal();
        loadJobs();
      } else {
        showMessage('error', `Failed to ${editingJob ? 'update' : 'create'} notification job`);
      }
    } catch (error: any) {
      console.error('Failed to save notification job:', error);
      showMessage('error', error.message || 'Failed to save notification job');
    }
  };

  const handleDelete = async (jobId: string, jobName: string) => {
    if (!confirm(`Are you sure you want to delete the notification job "${jobName}"?`)) {
      return;
    }

    const success = await EmailNotificationJobService.deleteJob(jobId);
    if (success) {
      showMessage('success', 'Notification job deleted successfully');
      loadJobs();
    } else {
      showMessage('error', 'Failed to delete notification job');
    }
  };

  const handleToggle = async (jobId: string, currentStatus: boolean) => {
    const success = await EmailNotificationJobService.toggleJob(jobId, !currentStatus);
    if (success) {
      showMessage('success', `Notification job ${!currentStatus ? 'enabled' : 'disabled'}`);
      loadJobs();
    } else {
      showMessage('error', 'Failed to toggle notification job');
    }
  };

  if (loading) {
    return (
      <div className="border border-gray-200 rounded-lg p-6">
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
          <span className="ml-3 text-gray-600">Loading notification jobs...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="border border-gray-200 rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center">
          <Mail className="h-6 w-6 text-blue-600 mr-3" />
          <div>
            <h4 className="text-lg font-medium text-gray-900">Email Notifications</h4>
            <p className="text-sm text-gray-600">Manage email notification jobs for different events</p>
          </div>
        </div>
        <button
          onClick={openCreateModal}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <Plus className="h-4 w-4" />
          Add Email Notification
        </button>
      </div>

      {/* Message */}
      {message && (
        <div className={`mb-4 p-3 rounded-lg flex items-center ${
          message.type === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
        }`}>
          {message.type === 'success' ? (
            <CheckCircle className="h-5 w-5 mr-2" />
          ) : (
            <AlertCircle className="h-5 w-5 mr-2" />
          )}
          <span className="text-sm">{message.text}</span>
        </div>
      )}

      {/* Jobs List */}
      {jobs.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <Mail className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No notification jobs</h3>
          <p className="mt-1 text-sm text-gray-500">
            Get started by creating your first email notification job.
          </p>
          <button
            onClick={openCreateModal}
            className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <Plus className="h-4 w-4" />
            Add Email Notification
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {jobs.map((job) => (
            <div
              key={job.id}
              className={`border rounded-lg p-4 ${
                job.enabled ? 'border-gray-200 bg-white' : 'border-gray-200 bg-gray-50'
              }`}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3">
                    <h5 className="text-base font-medium text-gray-900">{job.job_name}</h5>
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      job.enabled ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                    }`}>
                      {job.enabled ? 'Enabled' : 'Disabled'}
                    </span>
                  </div>
                  
                  <div className="mt-2 space-y-1">
                    <div className="text-sm text-gray-600">
                      <span className="font-medium">Recipients:</span> {job.recipient_emails.join(', ')}
                    </div>
                    <div className="text-sm text-gray-600">
                      <span className="font-medium">Events:</span>{' '}
                      {job.event_subscriptions.map(event => 
                        EVENT_OPTIONS.find(e => e.value === event)?.label || event
                      ).join(', ')}
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-2 ml-4">
                  <button
                    onClick={() => handleToggle(job.id, job.enabled)}
                    className={`p-2 rounded-lg transition-colors ${
                      job.enabled 
                        ? 'text-green-600 hover:bg-green-50' 
                        : 'text-gray-400 hover:bg-gray-100'
                    }`}
                    title={job.enabled ? 'Disable' : 'Enable'}
                  >
                    <Power className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => openEditModal(job)}
                    className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                    title="Edit"
                  >
                    <Edit2 className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(job.id, job.job_name)}
                    className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    title="Delete"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">
                {editingJob ? 'Edit Email Notification Job' : 'Add Email Notification Job'}
              </h3>
              <button
                onClick={closeModal}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Job Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Job Name *
                </label>
                <input
                  type="text"
                  value={formData.job_name}
                  onChange={(e) => setFormData({ ...formData, job_name: e.target.value })}
                  placeholder="e.g., Marketing Team Notifications"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <p className="text-xs text-gray-500 mt-1">A descriptive name for this notification job</p>
              </div>

              {/* Recipient Emails */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Recipient Email Addresses *
                </label>
                <textarea
                  value={formData.recipient_emails}
                  onChange={(e) => setFormData({ ...formData, recipient_emails: e.target.value })}
                  placeholder="email1@example.com, email2@example.com; email3@example.com"
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <p className="text-xs text-gray-500 mt-1">Separate multiple emails with commas (,) or semicolons (;)</p>
              </div>

              {/* Event Subscriptions */}
              <SearchableMultiSelect
                label="Event Subscriptions *"
                options={EVENT_OPTIONS}
                selected={formData.event_subscriptions}
                onChange={(selected) => setFormData({ ...formData, event_subscriptions: selected as EmailNotificationEvent[] })}
                placeholder="Select events to subscribe to..."
              />

              {/* Enabled Toggle */}
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="enabled"
                  checked={formData.enabled}
                  onChange={(e) => setFormData({ ...formData, enabled: e.target.checked })}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <label htmlFor="enabled" className="ml-2 text-sm font-medium text-gray-700">
                  Enable this notification job
                </label>
              </div>
            </div>

            <div className="sticky bottom-0 bg-gray-50 px-6 py-4 flex justify-end gap-3 border-t border-gray-200">
              <button
                onClick={closeModal}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <Save className="h-4 w-4" />
                {editingJob ? 'Update Job' : 'Create Job'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
